import { useEffect, useState } from "react";
import QRCode from "qrcode";
import VehicleCard from "./VehicleCard";
import VehicleTable from "./VehicleTable";

import ExcelJS from "exceljs";
import moment from "moment";
import vehicleService from "../../services/vehicleService";
import { useVehicleLog } from "../../hooks/useVehicleLog";
import { Loader } from "../../utils/loader";
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  TextField,
  Grid,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Typography,
} from "@mui/material";
import {
  Close,
  Print,
  QrCode,
  Refresh,
  Search,
  FilterList,
  PointOfSale,
  ExitToApp,
  DirectionsCar,
} from "@mui/icons-material";
import Alert from "@mui/material/Alert";
import CircularProgress from "@mui/material/CircularProgress";
import Switch from "@mui/material/Switch";
import FormControlLabel from "@mui/material/FormControlLabel";
import { alpha } from "@mui/material";
import { useAuth } from "../../context/AuthContext";
import PlacasAutorizadas from "./AuthorizedPlates";
import CameraControl from "./VehicleBarrier";
import PaymentSettings from "./PaymentSettings";
import PlanSubscribersDialog from "./Dialogs/PlanSubscribersDialog";
import HorariosMisa from "./HorariosMisa";
import CierreCaja from "./CierreCaja";
import apiClient from "../../utils/axios";
import EntradaManualDialog from './Dialogs/EntradaManualDialog';
import PaymentDialog from "./Dialogs/PaymentDialog";
import FilterDrawer from "../Layout/FilterDrawer";
import { puedeModoGlobal } from "../../utils/rolesModoGlobal";


    
// ─── Empty state de vehículos ─────────────────────────────────────────────────
const ORANGE = '#F59E0B';
const PRIMARY = '#282626';

function VehicleEmptyState({ hasFilters }) {
  const color = hasFilters ? '#64748b' : ORANGE;
  return (
    <Box sx={{
      display: 'flex', flexDirection: 'column', alignItems: 'center',
      justifyContent: 'center', py: 8, bgcolor: 'white',
      borderRadius: 3, border: '1px solid #E5E5E5',
    }}>
      <Box sx={{ p: 2.5, borderRadius: '50%', bgcolor: alpha(color, 0.08), mb: 2 }}>
        <svg width="72" height="60" viewBox="0 0 72 60" fill="none">
          {/* Carrocería */}
          <rect x="6" y="28" width="60" height="20" rx="4"
            fill={alpha(color, 0.12)} stroke={alpha(color, 0.3)} strokeWidth="1.5" />
          {/* Techo */}
          <path d="M18 28 L24 14 L48 14 L54 28 Z"
            fill={alpha(color, 0.18)} stroke={alpha(color, 0.3)} strokeWidth="1.5" />
          {/* Ventanas */}
          <rect x="26" y="16" width="8" height="10" rx="2"
            fill={alpha(color, 0.15)} stroke={alpha(color, 0.2)} strokeWidth="1" />
          <rect x="38" y="16" width="8" height="10" rx="2"
            fill={alpha(color, 0.15)} stroke={alpha(color, 0.2)} strokeWidth="1" />
          {/* Ruedas */}
          <circle cx="20" cy="48" r="8" fill={alpha(color, 0.15)} stroke={alpha(color, 0.4)} strokeWidth="1.5">
            <animate attributeName="opacity" values="0.8;1;0.8" dur="2s" repeatCount="indefinite" />
          </circle>
          <circle cx="52" cy="48" r="8" fill={alpha(color, 0.15)} stroke={alpha(color, 0.4)} strokeWidth="1.5">
            <animate attributeName="opacity" values="0.8;1;0.8" dur="2s" begin="0.4s" repeatCount="indefinite" />
          </circle>
          <circle cx="20" cy="48" r="3" fill={alpha(color, 0.3)} />
          <circle cx="52" cy="48" r="3" fill={alpha(color, 0.3)} />
          {/* Faros */}
          <rect x="7" y="32" width="6" height="4" rx="1"
            fill={alpha(color, 0.5)} stroke={alpha(color, 0.3)} strokeWidth="1">
            <animate attributeName="opacity" values="0.5;1;0.5" dur="1.5s" repeatCount="indefinite" />
          </rect>
        </svg>
      </Box>
      <Typography variant="subtitle1" fontWeight={700} sx={{ color: PRIMARY, mb: 0.5 }}>
        {hasFilters ? 'Sin resultados' : 'Sin vehículos registrados'}
      </Typography>
      <Typography variant="body2" sx={{ color: '#999', textAlign: 'center', maxWidth: 280 }}>
        {hasFilters
          ? 'No se encontraron vehículos con los filtros aplicados. Intenta con otros criterios.'
          : 'Aún no se han registrado entradas. Los vehículos aparecerán aquí al registrar accesos.'}
      </Typography>
    </Box>
  );
}

const VehicleList = () => {
  const { user, clienteInfo } = useAuth();
  // Nombre del negocio para tickets/recibos: parqueadero, y su sucursal si
  // se está operando dentro de una (evita el branding "PARKEY" quemado).
  const nombreNegocio = clienteInfo?.sucursal?.nombre
    ? `${clienteInfo?.nombre || 'Parqueadero'} - ${clienteInfo.sucursal.nombre}`
    : (clienteInfo?.nombre || process.env.REACT_APP_NOMBRE_CONJUNTO || 'Parqueadero');
  const { vehicles,
  searchTerm,
  setSearchTerm,
  loading,
  searchVehicles,
  filters,
  ordenarPor,
  currentPage,
  perPage,
  totalVehicles,
  totalPages,
  goToPage,
  changePerPage,
  loadVehicles,
  loadAllVehiclesByDate,
  } = useVehicleLog();

  // Función helper para formatear fechas
  const formatDateTimeLocal = (dateTime) => {
    if (!dateTime) return '';
    return new Date(dateTime).toLocaleString('es-ES');
  };

  const [filter, setFilter] = useState("all");
  const [viewMode, setViewMode] = useState("table");
  const [paymentSettingsOpen, setPaymentSettingsOpen] = useState(false);
  const [planSubscribersOpen, setPlanSubscribersOpen] = useState(false);
  const [qrDialog, setQrDialog] = useState({
    open: false,
    vehicle: null,
  });
  const [placasAutorizadasOpen, setPlacasAutorizadasOpen] = useState(false);
  const [horariosMisaOpen, setHorariosMisaOpen] = useState(false);
  const [cierreCajaOpen, setCierreCajaOpen] = useState(false);
  const [cameraControlOpen, setCameraControlOpen] = useState(false);
  const [filtrosAbiertos, setFiltrosAbiertos] = useState(false);
  const [fechaDesde, setFechaDesde] = useState(
    moment().subtract(1, "month").format("YYYY-MM-DD")
  );
  const [qrScanInput, setQrScanInput] = useState('');
  const [paymentDialogOpen, setPaymentDialogOpen] = useState(false);
  const [vehicleToCharge, setVehicleToCharge] = useState(null);
  const [fechaHasta, setFechaHasta] = useState(moment().format("YYYY-MM-DD"));
  const [totalVehiculosDentro, setTotalVehiculosDentro] = useState(0);
  // Filtro por sucursal: solo tiene sentido en modo global (sin sucursal
  // fija) — con sucursal fija el backend ya filtra solo y no hay nada que
  // elegir. '' = todas las sucursales.
  const [sucursalFiltro, setSucursalFiltro] = useState('');
  const [sucursalesDisponibles, setSucursalesDisponibles] = useState([]);
  // Filtro por número de calza/puesto físico (asignado desde el POS).
  const [calzaFiltro, setCalzaFiltro] = useState('');
  const enModoGlobal = puedeModoGlobal(user) && user?.sucursal_id == null;

  useEffect(() => {
    if (!enModoGlobal) return;
    apiClient.get('/api/sucursales')
      .then(res => setSucursalesDisponibles(res.data?.data || []))
      .catch(() => setSucursalesDisponibles([]));
  }, [enModoGlobal]);
  const [entradaManualOpen, setEntradaManualOpen] = useState(false);
  const [salidaMasivaOpen, setSalidaMasivaOpen] = useState(false);
  const [salidaMasivaRango, setSalidaMasivaRango] = useState('hoy');
  const [salidaMasivaFechaDesde, setSalidaMasivaFechaDesde] = useState('');
  const [salidaMasivaFechaHasta, setSalidaMasivaFechaHasta] = useState('');
  const [salidaMasivaPreview, setSalidaMasivaPreview] = useState(null);
  const [salidaMasivaLoadingPreview, setSalidaMasivaLoadingPreview] = useState(false);
  const [salidaMasivaConfirming, setSalidaMasivaConfirming] = useState(false);
  const [salidaMasivaResult, setSalidaMasivaResult] = useState(null);
  const [salidaMasivaSaldoPendiente, setSalidaMasivaSaldoPendiente] = useState(true);
  const [salidaMasivaIdentificadores, setSalidaMasivaIdentificadores] = useState('');
  const [salidaMasivaNoEncontrados, setSalidaMasivaNoEncontrados] = useState(null);
  // Sin matchear searchTerm acá: eso filtraba la tabla en cada letra
  // tecleada, sin esperar al clic en "Buscar" — la placa ya se manda al
  // servidor en handleSearch() (ver el botón/Enter), que es la única
  // búsqueda real por placa. Esto solo filtra por estado lo que ya vino
  // cargado, para la vista Dentro/Salieron/Todos.
  const filteredByStatus = vehicles.filter((vehicle) => {
    if (filter === "active") return vehicle.estado === "dentro";
    if (filter === "exited") return vehicle.estado === "fuera";
    return true;
  });



  useEffect(() => {
    const cargarTotalVehiculosDentro = async () => {
      try {
        const response = await loadAllVehiclesByDate({
          estado: "dentro",
        });
        setTotalVehiculosDentro(response);
      } catch (error) {
        console.error("Error cargando total de vehículos dentro:", error);
      }
    };

    cargarTotalVehiculosDentro();
    
    const interval = setInterval(cargarTotalVehiculosDentro, 30000);
    
    return () => clearInterval(interval);
  }, [loadAllVehiclesByDate]);


  const handleSearch = async () => {
    const searchParams = {
      fecha_desde: fechaDesde,
      fecha_hasta: fechaHasta,
      placa: searchTerm.trim() || undefined,
      numero_calza: calzaFiltro.trim() || undefined,
      estado:
        filter === "active"
          ? "dentro"
          : filter === "exited"
          ? "fuera"
          : undefined,
      sucursal_id: sucursalFiltro || undefined,
    };

    console.log("🔍 Buscando con parámetros:", searchParams);

    try {
      const result = await searchVehicles(searchParams);
      if (result.success) {
        console.log(
          "✅ Búsqueda exitosa:",
          result?.data?.length,
          "vehículos encontrados"
        );
      } else {
        console.error("❌ Error en búsqueda:", result.error);
      }
    } catch (error) {
      console.error("❌ Error ejecutando búsqueda:", error);
    }
  };

  // Quitar el chip de sucursal debe volver de inmediato al filtro global
  // (todas las sucursales), no solo limpiar el estado — sin esto, la lista
  // seguía mostrando el resultado ya filtrado hasta la próxima búsqueda.
  const handleQuitarSucursal = async () => {
    setSucursalFiltro("");
    try {
      const searchParams = {
        fecha_desde: fechaDesde,
        fecha_hasta: fechaHasta,
        placa: searchTerm.trim() || undefined,
        estado:
          filter === "active" ? "dentro" : filter === "exited" ? "fuera" : undefined,
        sucursal_id: undefined,
      };
      await searchVehicles(searchParams);
    } catch (error) {
      console.error("Error quitando filtro de sucursal", error);
    }
  };

  const handleResetFilters = async () => {
    const nuevaFechaDesde = moment().subtract(1, "month").format("YYYY-MM-DD");
    const nuevaFechaHasta = moment().format("YYYY-MM-DD");

    setSearchTerm("");
    setFilter("all");
    setFechaDesde(nuevaFechaDesde);
    setFechaHasta(nuevaFechaHasta);
    setSucursalFiltro("");
    setCalzaFiltro("");

    try {
      const searchParams = {
        fecha_desde: nuevaFechaDesde,
        fecha_hasta: nuevaFechaHasta,
        placa: undefined,
        numero_calza: undefined,
        estado: undefined,
        sucursal_id: undefined,
      };

      const result = await searchVehicles(searchParams);
      if (!result?.success) {
        await loadVehicles(1, false);
      }
    } catch (error) {
      console.error("Error reiniciando filtros", error);
      await loadVehicles(1, false);
    }
  };

  const generateQRCode = async (text) => {
    try {
      // Alta resolución para impresora térmica — el browser escala al tamaño CSS
      // pero la imagen nativa grande + image-rendering:pixelated evita el blur
      const qrDataUrl = await QRCode.toDataURL(text, {
        width: 1024,
        margin: 1,
        errorCorrectionLevel: 'H',
        color: {
          dark: "#000000",
          light: "#FFFFFF",
        },
      });
      return qrDataUrl;
    } catch (error) {
      console.error("Error generando QR:", error);
      return null;
    }
  };

  const handlePrintQR = async (vehicle) => {
    const qrDataUrl = await generateQRCode(vehicle.codigo_qr);
    setQrDialog({
      open: true,
      vehicle: vehicle,
      qrDataUrl: qrDataUrl,
    });
  };

  const printQRCode = () => {
  if (!qrDialog.qrDataUrl) return;

  const printWindow = window.open("", "_blank", "width=400,height=600");

  printWindow.document.write(`
    <!DOCTYPE html>
    <html>
    <head>
      <title>QR</title>
      <style>
        * { margin: 0; padding: 0; box-sizing: border-box; }
        @page { margin: 0; size: 58mm auto; }

        /* Arial/sans-serif + bold mínimo → agujas de la térmica imprimen más
           grueso y nítido que Courier New en tamaños pequeños              */
        body {
          font-family: Arial, 'Arial Black', Helvetica, sans-serif;
          width: 54mm;
          margin: 0 auto;
          padding: 2mm 2mm;
          background: #fff;
          color: #000;
          font-size: 10pt;
          font-weight: bold;
          -webkit-print-color-adjust: exact;
          print-color-adjust: exact;
        }

        .company {
          font-size: 14pt;
          font-weight: 900;
          text-align: center;
          text-transform: uppercase;
          letter-spacing: 3px;
          margin-bottom: 1mm;
        }
        .subtitle {
          font-size: 9pt;
          font-weight: bold;
          text-align: center;
          margin-bottom: 2mm;
        }
        .divider {
          border: none;
          border-top: 2px solid #000;
          margin: 2mm 0;
        }

        /* Código QR en texto — fuente monoespaciada grande */
        .code {
          font-family: 'Courier New', Courier, monospace;
          font-size: 12pt;
          font-weight: 900;
          text-align: center;
          letter-spacing: 2px;
          margin: 2mm 0 1mm;
        }

        /* Imagen QR — tamaño suficiente para lector pequeño, no todo el
           ancho del ticket. Se genera a 1024px y el browser la achica, así
           que reducir esto no pierde nitidez. */
        .qr-img {
          display: block;
          margin: 2mm auto;
          width: 26mm;
          height: 26mm;
          image-rendering: -moz-crisp-edges;
          image-rendering: -webkit-optimize-contrast;
          image-rendering: crisp-edges;
          image-rendering: pixelated;
        }

        /* Filas de datos: etiqueta negrita + valor normal */
        .row {
          display: flex;
          justify-content: space-between;
          align-items: baseline;
          margin: 1.5mm 0;
          font-size: 10pt;
          line-height: 1.3;
        }
        .label {
          font-weight: 900;
          text-transform: uppercase;
          font-size: 9pt;
          flex-shrink: 0;
          margin-right: 2mm;
        }
        .value {
          text-align: right;
          font-weight: bold;
          font-size: 10pt;
          word-break: break-all;
        }

        /* Placa destacada */
        .placa-value {
          font-family: 'Courier New', Courier, monospace;
          font-size: 13pt;
          font-weight: 900;
          letter-spacing: 1px;
        }

        .footer {
          font-size: 8pt;
          font-weight: bold;
          text-align: center;
          margin-top: 3mm;
        }

        @media print {
          body { width: 54mm; }
        }
      </style>
    </head>
    <body>
      <div class="company">${nombreNegocio}</div>
      <hr class="divider"/>

      <div class="code">${qrDialog.vehicle.codigo_qr}</div>
      <img class="qr-img" src="${qrDialog.qrDataUrl}" alt="QR"/>

      <hr class="divider"/>

      <div class="row">
        <span class="label">Placa</span>
        <span class="value placa-value">${qrDialog.vehicle.numero_placa || qrDialog.vehicle.numero_placa_auxiliar || 'N/A'}</span>
      </div>
      <div class="row">
        <span class="label">Color</span>
        <span class="value">${qrDialog.vehicle.color_vehiculo || 'N/A'}</span>
      </div>
      <div class="row">
        <span class="label">Tipo</span>
        <span class="value">${qrDialog.vehicle.tipo_vehiculo_label || qrDialog.vehicle.tipo_vehiculo || 'N/A'}</span>
      </div>
      <div class="row">
        <span class="label">Estado</span>
        <span class="value">${qrDialog.vehicle.estado === 'dentro' ? 'En parqueo' : 'Fuera'}</span>
      </div>
      <div class="row">
        <span class="label">Entrada</span>
        <span class="value">${formatDateTimeLocal(qrDialog.vehicle.fecha_entrada)}</span>
      </div>

      <hr class="divider"/>
      <div class="footer">${new Date().toLocaleString('es-ES')}</div>

      <script>
        window.onload = function() {
          setTimeout(function() {
            window.print();
            window.close();
          }, 800);
        };
      </script>
    </body>
    </html>
  `);

  printWindow.document.close();
  setQrDialog({ open: false, vehicle: null, qrDataUrl: null });
};

  // ── Reimpresión de recibo de pago ────────────────────────────────────────────
  const reimprimirRecibo = async (vehicle) => {
    try {
      const res = await apiClient.get(`/api/vehiculos/${vehicle.id}/datos-cobro`);
      if (!res.data?.success || !res.data?.data) {
        alert('No se encontró el cobro para este vehículo.');
        return;
      }
      const d = res.data.data;
      const nombre = nombreNegocio;
      const fmt = (n) => `$${Number(n ?? 0).toFixed(2)}`;
      const fmtDate = (iso) => iso ? new Date(iso).toLocaleString('es-EC', { timeZone: 'America/Guayaquil', day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '—';

      const pw = window.open('', '_blank', 'width=400,height=700');
      pw.document.write(`<!DOCTYPE html><html><head><title>Recibo</title><style>
        *{margin:0;padding:0;box-sizing:border-box}
        @page{margin:0;size:58mm auto}
        body{font-family:Arial,Helvetica,sans-serif;width:54mm;margin:0 auto;padding:2mm;background:#fff;color:#000;font-size:10pt;font-weight:bold;-webkit-print-color-adjust:exact;print-color-adjust:exact}
        .company{font-size:14pt;font-weight:900;text-align:center;text-transform:uppercase;letter-spacing:3px;margin-bottom:1mm}
        .subtitle{font-size:9pt;text-align:center;margin-bottom:2mm}
        .divider{border:none;border-top:2px solid #000;margin:2mm 0}
        .row{display:flex;justify-content:space-between;margin:1.5mm 0;font-size:10pt;line-height:1.3}
        .label{font-weight:900;text-transform:uppercase;font-size:9pt;flex-shrink:0;margin-right:2mm}
        .value{text-align:right;font-weight:bold;font-size:10pt;word-break:break-all}
        .placa{font-family:'Courier New',monospace;font-size:13pt;font-weight:900;letter-spacing:1px}
        .total{font-size:14pt;font-weight:900;text-align:center;margin:2mm 0;padding:2mm;border:2px solid #000}
        .gratis{font-size:12pt;font-weight:900;text-align:center;margin:2mm 0;padding:2mm;border:2px solid #000}
        .footer{font-size:8pt;text-align:center;margin-top:3mm}
        .reimpresion{font-size:8pt;font-weight:bold;text-align:center;border:1px dashed #000;padding:1mm;margin:2mm 0}
        @media print{body{width:54mm}}
      </style></head><body>
        <div class="company">${nombre}</div>
        <div class="reimpresion">*** REIMPRESIÓN ***</div>
        <hr class="divider"/>
        <div class="row"><span class="label">Placa</span><span class="value placa">${d.numero_placa || d.numero_placa_auxiliar || 'N/A'}</span></div>
        <div class="row"><span class="label">Entrada</span><span class="value">${fmtDate(d.fecha_entrada)}</span></div>
        <div class="row"><span class="label">Pago</span><span class="value">${fmtDate(d.fecha_pago)}</span></div>
        <div class="row"><span class="label">Permanencia</span><span class="value">${Number(d.horas_permanencia || 0).toFixed(2)} h</span></div>
        <div class="row"><span class="label">Horas cobradas</span><span class="value">${Number(d.horas_cobradas || 0).toFixed(2)} h</span></div>
        ${d.horas_descontadas > 0 ? `<div class="row"><span class="label">Descuento</span><span class="value">-${Number(d.horas_descontadas).toFixed(2)} h</span></div>` : ''}
        ${d.tarifa_grupo_nombre ? `<div class="row"><span class="label">Tarifa</span><span class="value">${d.tarifa_grupo_nombre}${d.tarifa_categoria_label ? ` (${d.tarifa_categoria_label})` : ''}</span></div>` : ''}
        <div class="row"><span class="label">Tipo</span><span class="value">${d.tipo === 'gratis' ? 'GRATIS' : d.tipo === 'plan' ? 'PLAN' : d.tipo === 'lost_ticket' ? 'TICKET PERDIDO' : 'NORMAL'}</span></div>
        ${d.nombre ? `<div class="row"><span class="label">Cliente</span><span class="value">${d.nombre}</span></div>` : ''}
        ${d.identificacion ? `<div class="row"><span class="label">ID</span><span class="value">${d.identificacion}</span></div>` : ''}
        ${d.metodo ? `<div class="row"><span class="label">Método</span><span class="value">${d.metodo.toUpperCase()}</span></div>` : ''}
        <hr class="divider"/>
        ${d.tipo === 'gratis' || Number(d.valor) === 0
          ? `<div class="gratis">SIN COSTO</div>`
          : `<div class="total">TOTAL: ${fmt(d.valor)}</div>`
        }
        <hr class="divider"/>
        <div class="footer">${nombre} — ${new Date().toLocaleString('es-EC')}</div>
        <script>window.onload=function(){setTimeout(function(){window.print();window.close();},600);};</script>
      </body></html>`);
      pw.document.close();
    } catch (e) {
      console.error(e);
      alert('Error al reimprimir el recibo.');
    }
  };

  const handleQRScan = async (qrCode) => {
    const codigo = qrCode.trim();
    try {
      let foundVehicle = vehicles.find(v => v.codigo_qr === codigo);
      let noEsTicketNormal = false;

      // Si no está en la tabla, consultar directamente al backend
      if (!foundVehicle) {
        try {
          const res = await apiClient.get(`/api/vehiculos/buscar/${encodeURIComponent(codigo)}`);
          if (res?.data?.success && res.data.data) {
            foundVehicle = res.data.data;
            // Refrescar la tabla para que el vehículo aparezca si corresponde
            loadVehicles(1, false);
          }
        } catch (buscarError) {
          if (buscarError.response?.status === 404) {
            noEsTicketNormal = true;
          } else {
            throw buscarError;
          }
        }
      }

      if (foundVehicle) {
        if (foundVehicle.estado !== 'dentro') {
          alert(`⚠️ El vehículo ya salió: ${foundVehicle.numero_placa || foundVehicle.numero_placa_auxiliar || codigo}`);
          setQrScanInput('');
          return;
        }

        if (foundVehicle.pagado) {
          alert(`✅ Vehículo ya pagado: ${foundVehicle.numero_placa || foundVehicle.numero_placa_auxiliar}`);
          setQrScanInput('');
          return;
        }

        setVehicleToCharge(foundVehicle);
        setPaymentDialogOpen(true);
      } else if (noEsTicketNormal) {
        // No es un ticket de entrada normal — puede ser el QR fijo de una
        // placa con plan/tarjeta. registrarSalida ahora reconoce ambos tipos.
        const result = await vehicleService.registrarSalida(codigo);
        if (result.success) {
          alert(`✅ Salida por plan registrada: ${result.data?.numero_placa || ''}`);
          loadVehicles(1, false);
        } else if (result.requierePago) {
          alert(`⚠️ ${result.error} — se abrirá el cobro para este vehículo.`);
          setVehicleToCharge(result.vehiculo);
          setPaymentDialogOpen(true);
        } else {
          alert('❌ QR no encontrado en el sistema');
        }
      } else {
        alert('❌ QR no encontrado en el sistema');
      }
    } catch (error) {
      console.error('Error en escaneo:', error);
      alert('❌ Error al buscar el QR: ' + (error.response?.data?.message || error.message));
    }
    setQrScanInput('');
  };

  // Acepta placas/QR separados por coma, espacio o salto de línea — pegar
  // una lista desde otro lado (Excel, WhatsApp) no debería obligar a
  // reformatearla primero.
  const parseIdentificadores = (texto) =>
    (texto || '').split(/[\n,]+/).map((x) => x.trim()).filter(Boolean);

  const cargarPreviewSalidaMasiva = async (rango, fechaDesdeVal, fechaHastaVal, identificadoresVal) => {
    setSalidaMasivaLoadingPreview(true);
    setSalidaMasivaPreview(null);
    setSalidaMasivaNoEncontrados(null);
    try {
      const body = { rango };
      if (rango === 'personalizado') {
        body.fecha_desde = fechaDesdeVal;
        body.fecha_hasta = fechaHastaVal;
      } else if (rango === 'placas') {
        const identificadores = parseIdentificadores(identificadoresVal);
        if (identificadores.length === 0) {
          setSalidaMasivaLoadingPreview(false);
          return;
        }
        body.identificadores = identificadores;
      }
      const res = await apiClient.post('/api/vehiculos/salida-masiva/preview', body);
      if (res.data?.success) {
        setSalidaMasivaPreview(res.data.data || []);
        setSalidaMasivaNoEncontrados(res.data.no_encontrados || null);
      }
    } catch (e) {
      console.error('Error cargando preview salida masiva:', e);
    } finally {
      setSalidaMasivaLoadingPreview(false);
    }
  };

  const handleSalidaMasivaOpen = () => {
    setSalidaMasivaRango('hoy');
    setSalidaMasivaFechaDesde(moment().format('YYYY-MM-DD'));
    setSalidaMasivaFechaHasta(moment().format('YYYY-MM-DD'));
    setSalidaMasivaIdentificadores('');
    setSalidaMasivaNoEncontrados(null);
    setSalidaMasivaPreview(null);
    setSalidaMasivaResult(null);
    setSalidaMasivaSaldoPendiente(true);
    setSalidaMasivaOpen(true);
    cargarPreviewSalidaMasiva('hoy', '', '');
  };

  const handleSalidaMasivaRangoChange = (nuevoRango) => {
    setSalidaMasivaRango(nuevoRango);
    setSalidaMasivaPreview(null);
    setSalidaMasivaNoEncontrados(null);
    setSalidaMasivaResult(null);
    if (nuevoRango !== 'personalizado' && nuevoRango !== 'placas') {
      cargarPreviewSalidaMasiva(nuevoRango, '', '');
    }
  };

  const handleSalidaMasivaConfirmar = async () => {
    if (salidaMasivaRango === 'personalizado' && (!salidaMasivaFechaDesde || !salidaMasivaFechaHasta)) return;
    if (salidaMasivaRango === 'placas' && parseIdentificadores(salidaMasivaIdentificadores).length === 0) return;
    setSalidaMasivaConfirming(true);
    try {
      const body = { rango: salidaMasivaRango, saldo_pendiente: salidaMasivaSaldoPendiente };
      if (salidaMasivaRango === 'personalizado') {
        body.fecha_desde = salidaMasivaFechaDesde;
        body.fecha_hasta = salidaMasivaFechaHasta;
      } else if (salidaMasivaRango === 'placas') {
        body.identificadores = parseIdentificadores(salidaMasivaIdentificadores);
      }
      const res = await apiClient.post('/api/vehiculos/salida-masiva', body);
      if (res.data?.success) {
        const extra = res.data.vehiculos_con_cargo
          ? ` ${res.data.vehiculos_con_cargo} con saldo pendiente ($${res.data.total_cargado?.toFixed(2)}).`
          : '';
        setSalidaMasivaResult({ severity: 'success', message: `Salida registrada para ${res.data.procesados} vehículo(s).${extra}` });
        setSalidaMasivaPreview([]);
        loadVehicles(1, false);
      } else {
        setSalidaMasivaResult({ severity: 'error', message: res.data?.error || 'Error al procesar.' });
      }
    } catch (e) {
      setSalidaMasivaResult({ severity: 'error', message: 'Error de conexión.' });
    } finally {
      setSalidaMasivaConfirming(false);
    }
  };

  return (
    <Box sx={{ flex: 1, overflow: "auto", p: 3, bgcolor: "#F4F4F4" }}>
      {loading ? (
          <Paper sx={{ borderRadius: 3 }}>
            <Loader />
          </Paper>
        ) : (
          <>
            <Paper elevation={0} sx={{ mb: 3, borderRadius: 3, border: "1px solid #E5E5E5", overflow: "hidden" }}>
              {/* ── Barra de búsqueda ── */}
              <Box sx={{ p: 2, display: "flex", gap: 1, alignItems: "center", flexWrap: "wrap" }}>
                <TextField
                  placeholder="Buscar por placa, color, observaciones..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && handleSearch()}
                  InputProps={{ startAdornment: <InputAdornment position="start"><Search color="action" /></InputAdornment> }}
                  size="small"
                  sx={{ flex: 1, minWidth: 200, "& .MuiOutlinedInput-root": { borderRadius: 2, backgroundColor: "#f8f9fa" } }}
                />
                <Button variant="outlined" startIcon={<FilterList />} onClick={() => setFiltrosAbiertos(true)} size="small"
                  sx={{ borderRadius: 2, textTransform: "none", fontWeight: 600, borderColor: "#393939", color: "#393939", "&:hover": { bgcolor: "#393939", color: "white" } }}>
                  Filtros
                </Button>
                <Button variant="contained" startIcon={<Search />} onClick={handleSearch} size="small"
                  sx={{ borderRadius: 2, textTransform: "none", fontWeight: 600, bgcolor: "#282626", "&:hover": { bgcolor: "#1a1a1a" } }}>
                  Buscar
                </Button>
                {['admin', 'superadmin', 'supervisor'].includes(user?.role) && (
                  <Button
                    variant="contained"
                    startIcon={<PointOfSale />}
                    onClick={() => setCierreCajaOpen(true)}
                    size="small"
                    sx={{ borderRadius: 2, textTransform: "none", fontWeight: 600, bgcolor: "#F59E0B", "&:hover": { bgcolor: "#D97706" } }}
                  >
                    Cierre de caja
                  </Button>
                )}
                <Button
                  variant="outlined"
                  startIcon={<DirectionsCar />}
                  onClick={() => setEntradaManualOpen(true)}
                  size="small"
                  sx={{ borderRadius: 2, textTransform: "none", fontWeight: 600, borderColor: "#282626", color: "#282626", "&:hover": { bgcolor: "#282626", color: "white" } }}
                >
                  Registro manual
                </Button>
                {['admin', 'superadmin', 'supervisor'].includes(user?.role) && (
                  <Button
                    variant="outlined"
                    startIcon={<ExitToApp />}
                    onClick={handleSalidaMasivaOpen}
                    size="small"
                    sx={{ borderRadius: 2, textTransform: "none", fontWeight: 600, borderColor: "#ef4444", color: "#ef4444", "&:hover": { bgcolor: "#ef4444", color: "white" } }}
                  >
                    Salida automática
                  </Button>
                )}
              </Box>

              {/* ── Chips de filtros activos ── */}
              {/* ── Chips de filtros activos ── */}
              {(searchTerm || filter !== "all" || fechaDesde || fechaHasta || sucursalFiltro || calzaFiltro) && (
                <Box sx={{ px: 2, pb: 1.5, display: "flex", gap: 0.75, flexWrap: "wrap", alignItems: "center" }}>
                  <Typography variant="caption" sx={{ color: "#999", mr: 0.5, fontWeight: 600, textTransform: "uppercase", letterSpacing: 0.5 }}>Filtros:</Typography>
                  {searchTerm && (
                    <Chip size="small" label={`Placa: "${searchTerm}"`} onDelete={() => setSearchTerm("")}
                      sx={{ height: 24, fontSize: 11, fontWeight: 600, bgcolor: "#F59E0B", color: "white", "& .MuiChip-deleteIcon": { color: "rgba(255,255,255,0.7)" } }} />
                  )}
                  {filter !== "all" && (
                    <Chip size="small" label={filter === "active" ? "Dentro" : "Salieron"} onDelete={() => setFilter("all")}
                      sx={{ height: 24, fontSize: 11, fontWeight: 600, bgcolor: "#282626", color: "white", "& .MuiChip-deleteIcon": { color: "rgba(255,255,255,0.7)" } }} />
                  )}
                  {fechaDesde && (
                    <Chip size="small" label={`Desde: ${fechaDesde}`} onDelete={() => setFechaDesde("")}
                      sx={{ height: 24, fontSize: 11, fontWeight: 600, bgcolor: "#F4F4F4", color: "#282626", border: "1px solid #E5E5E5" }} />
                  )}
                  {fechaHasta && (
                    <Chip size="small" label={`Hasta: ${fechaHasta}`} onDelete={() => setFechaHasta("")}
                      sx={{ height: 24, fontSize: 11, fontWeight: 600, bgcolor: "#F4F4F4", color: "#282626", border: "1px solid #E5E5E5" }} />
                  )}
                  {sucursalFiltro && (
                    <Chip size="small"
                      label={`Sucursal: ${sucursalesDisponibles.find(s => String(s.id) === String(sucursalFiltro))?.nombre || sucursalFiltro}`}
                      onDelete={handleQuitarSucursal}
                      sx={{ height: 24, fontSize: 11, fontWeight: 600, bgcolor: "#3b82f6", color: "white", "& .MuiChip-deleteIcon": { color: "rgba(255,255,255,0.7)" } }} />
                  )}
                  {calzaFiltro && (
                    <Chip size="small" label={`Calza: "${calzaFiltro}"`} onDelete={() => setCalzaFiltro("")}
                      sx={{ height: 24, fontSize: 11, fontWeight: 600, bgcolor: "#10b981", color: "white", "& .MuiChip-deleteIcon": { color: "rgba(255,255,255,0.7)" } }} />
                  )}
                </Box>
              )}
            </Paper>

            {/* ── Escáner QR ── */}
            {user?.role !== "guard" && (
              <Paper elevation={0} sx={{ mb: 2, borderRadius: 2, border: "1px solid #E5E5E5", p: 2, bgcolor: "white" }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                  <Box sx={{ width: 36, height: 36, borderRadius: 2, bgcolor: "#F59E0B", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                    <QrCode sx={{ color: "white", fontSize: 20 }} />
                  </Box>
                  <TextField
                    fullWidth
                    placeholder="Escanear QR con pistola lectora y presionar ENTER..."
                    value={qrScanInput}
                    onChange={(e) => setQrScanInput(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        e.preventDefault();
                        if (qrScanInput.trim()) handleQRScan(qrScanInput.trim());
                      }
                    }}
                    autoFocus
                    size="small"
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        borderRadius: 2,
                        bgcolor: "#FAFAFA",
                        "& fieldset": { borderColor: "#F59E0B", borderWidth: 1.5 },
                        "&:hover fieldset": { borderColor: "#D97706" },
                        "&.Mui-focused fieldset": { borderColor: "#F59E0B", boxShadow: "0 0 0 3px rgba(245,158,11,0.15)" },
                      },
                    }}
                  />
                  <Button variant="contained" startIcon={<Refresh />} size="small"
                    onClick={() => loadVehicles(1, false)}
                    sx={{ flexShrink: 0, borderRadius: 2, textTransform: "none", fontWeight: 700, bgcolor: "#282626", "&:hover": { bgcolor: "#1a1a1a" }, px: 2 }}>
                    Actualizar
                  </Button>
                </Box>
              </Paper>
            )}


            {/* Contenido Principal */}
            {filteredByStatus.length === 0 ? (
              <VehicleEmptyState hasFilters={vehicles.length > 0} />
            ) : (
              <>
                {viewMode === "table" ? (
                  <VehicleTable
                    vehicles={filteredByStatus}
                    onPrintQR={handlePrintQR}
                    onReimprimirRecibo={reimprimirRecibo}
                    loadVehicles={loadVehicles}
                    user={user}
                    orderBy={filters.order_by}
                    orderDir={filters.order_dir}
                    onSort={ordenarPor}
                  />
                ) : (
                  <Grid container spacing={3}>
                    {filteredByStatus.map((vehicle) => (
                      <Grid item xs={12} sm={6} lg={4} key={vehicle.id}>
                        <VehicleCard vehicle={vehicle} />
                      </Grid>
                    ))}
                  </Grid>
                )}
              </>
            )}
          </>
        )}
        {!loading && vehicles.length > 0 && (
          <Paper sx={{ p: 2, mt: 3, borderRadius: 3 }}>
            <Grid container spacing={2} alignItems="center">
              {/* Información de registros */}
              <Grid item xs={12} md={4}>
                <Typography variant="body2" color="text.secondary">
                  Mostrando {((currentPage - 1) * perPage) + 1} - {Math.min(currentPage * perPage, totalVehicles)} de <strong>{totalVehicles.toLocaleString()}</strong> registros
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Página {currentPage} de {totalPages}
                </Typography>
              </Grid>

              {/* Selector de registros por página */}
              <Grid item xs={12} md={2}>
                <FormControl fullWidth size="small">
                  <InputLabel>Por página</InputLabel>
                  <Select
                    value={perPage}
                    label="Por página"
                    onChange={(e) => changePerPage(Number(e.target.value))}
                  >
                    <MenuItem value={10}>10</MenuItem>
                    <MenuItem value={25}>25</MenuItem>
                    <MenuItem value={50}>50</MenuItem>
                    <MenuItem value={100}>100</MenuItem>
                    <MenuItem value={200}>200</MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              {/* Botones de navegación */}
              <Grid item xs={12} md={6}>
                <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end', flexWrap: 'wrap' }}>
                  {/* Primera página */}
                  <Button
                    variant="outlined"
                    size="small"
                    disabled={currentPage === 1}
                    onClick={() => goToPage(1)}
                    sx={{
                      borderColor: '#d1d5db',
                      color: '#374151',
                      '&:disabled': { borderColor: '#e5e7eb', color: '#9ca3af' }
                    }}
                  >
                    Primera
                  </Button>

                  {/* Página anterior */}
                  <Button
                    variant="outlined"
                    size="small"
                    disabled={currentPage === 1}
                    onClick={() => goToPage(currentPage - 1)}
                    sx={{
                      borderColor: '#d1d5db',
                      color: '#374151',
                      '&:disabled': { borderColor: '#e5e7eb', color: '#9ca3af' }
                    }}
                  >
                    ‹ Anterior
                  </Button>
                  
                  {/* Números de página dinámicos */}
                  <Box sx={{ display: 'flex', gap: 0.5 }}>
                    {(() => {
                      const pages = [];
                      const showPages = 5; // Mostrar 5 páginas a la vez
                      let startPage = Math.max(1, currentPage - Math.floor(showPages / 2));
                      let endPage = Math.min(totalPages, startPage + showPages - 1);
                      
                      // Ajustar si estamos cerca del final
                      if (endPage - startPage < showPages - 1) {
                        startPage = Math.max(1, endPage - showPages + 1);
                      }
                      
                      for (let i = startPage; i <= endPage; i++) {
                        pages.push(
                          <Button
                            key={i}
                            variant={currentPage === i ? "contained" : "outlined"}
                            size="small"
                            onClick={() => goToPage(i)}
                            sx={{ 
                              minWidth: 40,
                              bgcolor: currentPage === i ? '#393939' : 'transparent',
                              borderColor: currentPage === i ? '#393939' : '#d1d5db',
                              color: currentPage === i ? 'white' : '#374151',
                              '&:hover': {
                                bgcolor: currentPage === i ? '#2a2a2a' : 'rgba(0,0,0,0.04)',
                                borderColor: currentPage === i ? '#2a2a2a' : '#9ca3af'
                              }
                            }}
                          >
                            {i}
                          </Button>
                        );
                      }
                      return pages;
                    })()}
                  </Box>

                  {/* Página siguiente */}
                  <Button
                    variant="outlined"
                    size="small"
                    disabled={currentPage === totalPages}
                    onClick={() => goToPage(currentPage + 1)}
                    sx={{
                      borderColor: '#d1d5db',
                      color: '#374151',
                      '&:disabled': { borderColor: '#e5e7eb', color: '#9ca3af' }
                    }}
                  >
                    Siguiente ›
                  </Button>

                  {/* Última página */}
                  <Button
                    variant="outlined"
                    size="small"
                    disabled={currentPage === totalPages}
                    onClick={() => goToPage(totalPages)}
                    sx={{
                      borderColor: '#d1d5db',
                      color: '#374151',
                      '&:disabled': { borderColor: '#e5e7eb', color: '#9ca3af' }
                    }}
                  >
                    Última
                  </Button>
                </Box>
              </Grid>
            </Grid>
          </Paper>
        )}
      <Dialog
        open={qrDialog.open}
        onClose={() =>
          setQrDialog({ open: false, vehicle: null, qrDataUrl: null })
        }
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            background: "linear-gradient(135deg, #393939 0%, #4a4a4a 100%)",
            color: "white",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <QrCode />
            Código QR - {qrDialog.vehicle?.codigo_qr}
          </Box>
          <IconButton
            onClick={() =>
              setQrDialog({ open: false, vehicle: null, qrDataUrl: null })
            }
            size="small"
            sx={{ color: "white" }}
          >
            <Close />
          </IconButton>
        </DialogTitle>

        <DialogContent sx={{ textAlign: "center", py: 4 }}>
          {qrDialog.vehicle && qrDialog.qrDataUrl && (
            <>
              <Box
                sx={{
                  mb: 3,
                  p: 2,
                  border: "2px solid #e0e0e0",
                  borderRadius: 2,
                  display: "inline-block",
                }}
              >
                <img
                  src={qrDialog.qrDataUrl}
                  alt="QR Code"
                  style={{
                    width: "200px",
                    height: "200px",
                    border: "1px solid #ddd",
                    borderRadius: "5px",
                  }}
                />
              </Box>

              <Typography
                variant="h4"
                sx={{
                  mb: 3,
                  fontWeight: "bold",
                  letterSpacing: "2px",
                  color: "#333",
                }}
              >
                {qrDialog.vehicle.codigo_qr}
              </Typography>

              <Paper
                sx={{ p: 2, maxWidth: "300px", mx: "auto", bgcolor: "#f5f5f5" }}
              >
                <Typography
                  variant="subtitle2"
                  sx={{ mb: 2, fontWeight: "bold" }}
                >
                  Información del Vehículo
                </Typography>
                <Box sx={{ textAlign: "left" }}>
                  <Typography variant="body2" sx={{ mb: 1 }}>
                    <strong>Placa:</strong>{" "}
                    {qrDialog.vehicle.numero_placa ||
                      qrDialog.vehicle.numero_placa_auxiliar ||
                      "No especificada"}
                  </Typography>
                  <Typography variant="body2" sx={{ mb: 1 }}>
                    <strong>Color:</strong>{" "}
                    {qrDialog.vehicle.color_vehiculo || "N/A"}
                  </Typography>
                  <Typography variant="body2" sx={{ mb: 1 }}>
                    <strong>Tipo:</strong>{" "}
                    {qrDialog.vehicle.tipo_vehiculo_label || qrDialog.vehicle.tipo_vehiculo || "N/A"}
                  </Typography>
                  <Typography variant="body2" sx={{ mb: 1 }}>
                    <strong>Estado:</strong>{" "}
                    {qrDialog.vehicle.estado === "dentro"
                      ? "En instalaciones"
                      : "Fuera"}
                  </Typography>
                  <Typography variant="body2">
                    <strong>Entrada:</strong>{" "}
                    {formatDateTimeLocal(qrDialog.vehicle.fecha_entrada)}
                  </Typography>
                </Box>
              </Paper>
            </>
          )}

          {qrDialog.vehicle && !qrDialog.qrDataUrl && (
            <Typography variant="body1" color="error">
              Error generando el código QR
            </Typography>
          )}
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 3 }}>
          <Button
            onClick={() =>
              setQrDialog({ open: false, vehicle: null, qrDataUrl: null })
            }
            color="inherit"
          >
            Cancelar
          </Button>
          <Button
            onClick={printQRCode}
            variant="contained"
            startIcon={<Print />}
            disabled={!qrDialog.qrDataUrl}
            sx={{
              background: "#393939ff",
              "&:hover": { background: "#2a2a2a" },
              "&:disabled": { background: "#ccc" },
            }}
          >
            Imprimir QR
          </Button>
        </DialogActions>
      </Dialog>

      <PlacasAutorizadas
        open={placasAutorizadasOpen}
        onClose={() => setPlacasAutorizadasOpen(false)}
      />
      <HorariosMisa
        open={horariosMisaOpen}
        onClose={() => setHorariosMisaOpen(false)}
      />
      <CierreCaja
        open={cierreCajaOpen}
        onClose={() => setCierreCajaOpen(false)}
        soloTurno
      />
      <CameraControl
        open={cameraControlOpen}
        onClose={() => setCameraControlOpen(false)}
      />
      <PaymentSettings
        open={paymentSettingsOpen}
        onClose={() => setPaymentSettingsOpen(false)}
        vehicles={totalVehiculosDentro}
      />
      <PlanSubscribersDialog
        open={planSubscribersOpen}
        onClose={() => setPlanSubscribersOpen(false)}
      />
      <EntradaManualDialog
        open={entradaManualOpen}
        onClose={() => setEntradaManualOpen(false)}
        onRegistrado={() => loadVehicles(1, false)}
      />

      <PaymentDialog
        open={paymentDialogOpen}
        onClose={() => {
          setPaymentDialogOpen(false);
          setVehicleToCharge(null);
          loadVehicles(1, false);
        }}
        onPaymentSuccess={() => {
          loadVehicles(1, false);
        }}
        vehicle={vehicleToCharge}
        loadVehicles={loadVehicles}
      />
      <FilterDrawer
        open={filtrosAbiertos}
        onClose={() => setFiltrosAbiertos(false)}
        searchTerm={searchTerm}
        setSearchTerm={setSearchTerm}
        fechaDesde={fechaDesde}
        setFechaDesde={setFechaDesde}
        fechaHasta={fechaHasta}
        setFechaHasta={setFechaHasta}
        filter={filter}
        setFilter={setFilter}
        onSearch={handleSearch}
        onReset={handleResetFilters}
        sucursalFiltro={sucursalFiltro}
        setSucursalFiltro={setSucursalFiltro}
        sucursalesDisponibles={enModoGlobal ? sucursalesDisponibles : null}
        calzaFiltro={calzaFiltro}
        setCalzaFiltro={setCalzaFiltro}
      />

      {/* ── Diálogo: Salida automática ── */}
      <Dialog open={salidaMasivaOpen} onClose={() => setSalidaMasivaOpen(false)} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', bgcolor: '#282626', color: 'white', px: 3, py: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <ExitToApp />
            Salida automática de vehículos
          </Box>
          <IconButton onClick={() => setSalidaMasivaOpen(false)} size="small" sx={{ color: 'white' }}>
            <Close />
          </IconButton>
        </DialogTitle>

        <DialogContent sx={{ px: 3, pt: 3, pb: 1 }}>
          <Typography variant="body2" sx={{ color: '#666', mb: 2 }}>
            Selecciona el rango de vehículos que deseas marcar como salidos:
          </Typography>

          {/* Chips de rango */}
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 3 }}>
            {[
              { value: 'hoy', label: 'Hoy' },
              { value: 'semana', label: 'Esta semana' },
              { value: 'mes', label: 'Este mes' },
              { value: 'personalizado', label: 'Personalizado' },
              { value: 'placas', label: 'Placas o QR específicos' },
            ].map(({ value, label }) => (
              <Chip
                key={value}
                label={label}
                onClick={() => handleSalidaMasivaRangoChange(value)}
                variant={salidaMasivaRango === value ? 'filled' : 'outlined'}
                sx={{
                  fontWeight: 600,
                  cursor: 'pointer',
                  bgcolor: salidaMasivaRango === value ? '#282626' : 'transparent',
                  color: salidaMasivaRango === value ? 'white' : '#282626',
                  borderColor: '#282626',
                  '&:hover': { bgcolor: salidaMasivaRango === value ? '#1a1a1a' : alpha('#282626', 0.08) },
                }}
              />
            ))}
          </Box>

          {/* Fechas personalizadas */}
          {salidaMasivaRango === 'personalizado' && (
            <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
              <TextField
                label="Desde"
                type="date"
                size="small"
                value={salidaMasivaFechaDesde}
                onChange={(e) => setSalidaMasivaFechaDesde(e.target.value)}
                InputLabelProps={{ shrink: true }}
                fullWidth
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
              />
              <TextField
                label="Hasta"
                type="date"
                size="small"
                value={salidaMasivaFechaHasta}
                onChange={(e) => setSalidaMasivaFechaHasta(e.target.value)}
                InputLabelProps={{ shrink: true }}
                fullWidth
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
              />
              <Button
                variant="outlined"
                size="small"
                onClick={() => cargarPreviewSalidaMasiva('personalizado', salidaMasivaFechaDesde, salidaMasivaFechaHasta)}
                disabled={!salidaMasivaFechaDesde || !salidaMasivaFechaHasta}
                sx={{ flexShrink: 0, borderRadius: 2, textTransform: 'none', fontWeight: 600, borderColor: '#282626', color: '#282626' }}
              >
                Buscar
              </Button>
            </Box>
          )}

          {/* Placas o QR puntuales */}
          {salidaMasivaRango === 'placas' && (
            <Box sx={{ mb: 2 }}>
              <TextField
                label="Placas o códigos QR"
                placeholder={'Una por línea, o separadas por coma\nEj: ABC1234, PDT4545\n9f3a7c1e2b...'}
                size="small"
                multiline
                minRows={3}
                value={salidaMasivaIdentificadores}
                onChange={(e) => setSalidaMasivaIdentificadores(e.target.value)}
                fullWidth
                helperText="Acepta placas (con o sin guion) y códigos QR mezclados"
                sx={{ mb: 1, '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
              />
              <Button
                variant="outlined"
                size="small"
                onClick={() => cargarPreviewSalidaMasiva('placas', '', '', salidaMasivaIdentificadores)}
                disabled={parseIdentificadores(salidaMasivaIdentificadores).length === 0}
                sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 600, borderColor: '#282626', color: '#282626' }}
              >
                Buscar
              </Button>
              {salidaMasivaNoEncontrados && salidaMasivaNoEncontrados.length > 0 && (
                <Alert severity="warning" sx={{ mt: 1.5, borderRadius: 2 }}>
                  No se encontraron dentro del parqueadero: {salidaMasivaNoEncontrados.join(', ')}
                </Alert>
              )}
            </Box>
          )}

          {/* Saldo pendiente: carga lo que deba cada vehículo a su cuenta en
              vez de cerrarlo gratis — se cobra la próxima vez que entre. */}
          <Box sx={{ mb: 2, p: 1.5, border: '1px solid #E5E5E5', borderRadius: 2, bgcolor: '#fafafa' }}>
            <FormControlLabel
              control={<Switch checked={salidaMasivaSaldoPendiente}
                onChange={(e) => setSalidaMasivaSaldoPendiente(e.target.checked)} />}
              label={<Typography variant="body2" fontWeight={600}>
                {salidaMasivaSaldoPendiente ? 'Con saldo pendiente' : 'Cierra gratis, sin cobrar'}
              </Typography>}
            />
            <Typography variant="caption" sx={{ color: '#888', display: 'block', mt: 0.5 }}>
              {salidaMasivaSaldoPendiente
                ? 'A cada vehículo se le carga lo que deba (tarifa normal) a su saldo pendiente. Las placas autorizadas nunca generan cargo.'
                : 'Los vehículos salen sin cobrarles nada, ni ahora ni después.'}
            </Typography>
          </Box>

          {/* Resultado de la búsqueda */}
          {salidaMasivaResult && (
            <Alert severity={salidaMasivaResult.severity} sx={{ mb: 2, borderRadius: 2 }}>
              {salidaMasivaResult.message}
            </Alert>
          )}

          {salidaMasivaLoadingPreview ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
              <CircularProgress size={28} sx={{ color: '#F59E0B' }} />
            </Box>
          ) : salidaMasivaPreview !== null && (
            <Box>
              <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1, color: salidaMasivaPreview.length === 0 ? '#16a34a' : '#ef4444' }}>
                {salidaMasivaPreview.length === 0
                  ? 'No hay vehículos dentro en este rango.'
                  : `${salidaMasivaPreview.length} vehículo(s) serán marcados como salidos:`}
              </Typography>
              {salidaMasivaPreview.length > 0 && (
                <Box sx={{ maxHeight: 200, overflowY: 'auto', border: '1px solid #E5E5E5', borderRadius: 2, p: 1 }}>
                  {salidaMasivaPreview.map((v) => (
                    <Box key={v.id} sx={{ display: 'flex', justifyContent: 'space-between', py: 0.5, px: 1, borderRadius: 1, '&:hover': { bgcolor: '#f5f5f5' } }}>
                      <Typography variant="body2" fontWeight={600}>{v.placa}</Typography>
                      <Typography variant="caption" sx={{ color: '#888' }}>
                        {v.fecha_entrada ? new Date(v.fecha_entrada).toLocaleString('es-ES') : ''}
                      </Typography>
                    </Box>
                  ))}
                </Box>
              )}
            </Box>
          )}
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 2.5, pt: 1.5 }}>
          <Button onClick={() => setSalidaMasivaOpen(false)} sx={{ color: '#888', borderRadius: 2 }}>
            Cancelar
          </Button>
          <Button
            variant="contained"
            onClick={handleSalidaMasivaConfirmar}
            disabled={salidaMasivaConfirming || !salidaMasivaPreview || salidaMasivaPreview.length === 0}
            startIcon={salidaMasivaConfirming ? <CircularProgress size={16} color="inherit" /> : <ExitToApp />}
            sx={{ bgcolor: '#ef4444', borderRadius: 2, fontWeight: 700, textTransform: 'none', '&:hover': { bgcolor: '#dc2626' }, '&:disabled': { bgcolor: '#fca5a5', color: 'white' } }}
          >
            {salidaMasivaConfirming ? 'Procesando...' : `Confirmar salida (${salidaMasivaPreview?.length ?? 0})`}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default VehicleList;
